import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithPopup, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut, 
  onAuthStateChanged,
  updateProfile,
  getAdditionalUserInfo
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from '../firebase';
import { logEvent, EVENTS, flush as flushEvents } from '../services/eventLogger';

// Admin status is determined exclusively by the Firebase { admin: true } custom
// claim (granted via the setAdminClaim Cloud Function). The legacy hardcoded-UID
// fallback was retired once all real admins were migrated to the claim.

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdminClaim, setIsAdminClaim] = useState(false);
  const [loading, setLoading] = useState(true);

  // Sign up with email/password
  const signup = async (email, password, displayName) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName) {
      await updateProfile(result.user, { displayName });
    }

    // Track new user signup in analytics via Cloud Function (keeps existing
    // aggregate doc updated) AND append to granular event stream.
    try {
      const token = await result.user.getIdToken();
      fetch('https://us-central1-investport-pro.cloudfunctions.net/trackDau', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ isNewUser: true }),
      }).catch(() => {});
    } catch(err) {}
    try { logEvent(EVENTS.SIGN_UP_COMPLETED, { source_screen: 'auth' }); } catch {}

    return result;
  };

  // Login with email/password
  const login = async (email, password) => {
    const res = await signInWithEmailAndPassword(auth, email, password);
    try { logEvent(EVENTS.LOGIN_COMPLETED, { method: 'password' }); } catch {}
    return res;
  };

  // Login with Google. Google uses ONE flow for both sign-in and sign-up, so we
  // detect brand-new accounts via getAdditionalUserInfo().isNewUser and track
  // them the same way email/password signup does — otherwise every Google
  // signup is invisible to the new-user analytics counter.
  const loginWithGoogle = async () => {
    const res = await signInWithPopup(auth, googleProvider);
    const isNewUser = getAdditionalUserInfo(res)?.isNewUser === true;
    if (isNewUser) {
      try {
        const token = await res.user.getIdToken();
        fetch('https://us-central1-investport-pro.cloudfunctions.net/trackDau', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ isNewUser: true }),
        }).catch(() => {});
      } catch (err) {}
      try { logEvent(EVENTS.SIGN_UP_COMPLETED, { source_screen: 'auth', method: 'google' }); } catch {}
    } else {
      try { logEvent(EVENTS.LOGIN_COMPLETED, { method: 'google' }); } catch {}
    }
    return res;
  };

  // Reset password
  const resetPassword = (email) => {
    return sendPasswordResetEmail(auth, email);
  };

  // Logout — flush buffered events before sign-out so we don't lose them.
  const logout = async () => {
    try { logEvent(EVENTS.LOGOUT); } catch {}
    try { await flushEvents(); } catch {}
    return signOut(auth);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);

      setLoading(false);

      // ── Everything below is NON-BLOCKING background work ──

      // Admin check via custom claim. getIdTokenResult() reads the locally-cached
      // token (no network round-trip), so this resolves near-instantly; admin is
      // cleared immediately when there's no user.
      if (user) {
        user.getIdTokenResult().then(result => {
          setIsAdminClaim(result.claims?.admin === true);
        }).catch(() => setIsAdminClaim(false));
      } else {
        setIsAdminClaim(false);
      }

      // Update email and lastLogin in Firestore for Admin Dashboard visualization
      if (user && user.email) {
        // Fire and forget — never block rendering
        const loginPayload = {
          email: user.email,
          lastLoginAt: new Date().toISOString()
        };
        // Always write createdAt from Auth metadata (immutable, = real signup date)
        if (user.metadata?.creationTime) {
          loginPayload.createdAt = user.metadata.creationTime;
        }
        setDoc(doc(db, 'users', user.uid), loginPayload, { merge: true }).catch(() => {});

        // ★ Smart DAU Tracker via Cloud Function
        const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });
        const trackKey = `dau_cf_${user.uid}`;
        const lastTracked = localStorage.getItem(trackKey);

        if (lastTracked !== todayStr) {
          localStorage.setItem(trackKey, todayStr);
          user.getIdToken().then(token => {
            fetch('https://us-central1-investport-pro.cloudfunctions.net/trackDau', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({}),
            }).then(res => {
              if (!res.ok) {
                console.error("trackDau failed:", res.status);
                localStorage.removeItem(trackKey);
              }
            }).catch((err) => {
              console.error("Failed to track DAU", err);
              localStorage.removeItem(trackKey);
            });
          }).catch(() => {
            localStorage.removeItem(trackKey);
          });
        }
      }
    });

    return unsubscribe;
  }, []);

  // Update user profile name
  const updateUserName = async (displayName) => {
    if (auth.currentUser) {
      await updateProfile(auth.currentUser, { displayName });
      // We must force the state to update to reflect changes immediately
      setCurrentUser({ ...auth.currentUser, displayName });
    }
  };

  const value = {
    currentUser,
    // Admin access is granted exclusively via the { admin: true } custom claim.
    isAdmin: isAdminClaim,
    login,
    signup,
    loginWithGoogle,
    resetPassword,
    logout,
    updateUserName,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
